/**
 * Shared utilities for Alternative Payment Methods (APM)
 * Used by Apple Pay, Google Pay, and PayPal integrations
 */

import { Country } from 'country-state-city';

/**
 * Notifies the application that a payment method is available or unavailable.
 * Dispatches a custom event that other parts of the app can listen for.
 *
 * @param {string} type - Payment method type ('applePay', 'googlePay', 'paypal')
 * @param {boolean} isAvailable - Whether the payment method is available
 */
export function notifyApmAvailability(type, isAvailable) {
  window.dispatchEvent(
    new CustomEvent('apm-availability', {
      detail: { type, available: isAvailable },
    }),
  );
}

/**
 * Creates a MutationObserver that watches a button's disabled attribute
 * and applies appropriate visual styling.
 *
 * @param {HTMLElement} button - The button element to observe
 * @returns {MutationObserver} The configured observer (already observing)
 */
export function createButtonStateObserver(button) {
  const observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (mutation.attributeName === 'disabled') {
        if (button.hasAttribute('disabled')) {
          button.style.opacity = '0.4';
          button.style.cursor = 'not-allowed';
          button.style.pointerEvents = 'none';
        } else {
          button.style.opacity = '1';
          button.style.cursor = 'pointer';
          button.style.pointerEvents = 'auto';
        }
      }
    });
  });

  observer.observe(button, { attributes: true });
  return observer;
}

/**
 * Looks up a human-readable country name from a country code.
 * Falls back gracefully if the country is not found.
 *
 * @param {string} countryCode - ISO country code (e.g., 'US', 'GB')
 * @param {string} defaultCode - Fallback code if countryCode is empty (default: 'US')
 * @returns {string} The country name, or the code itself if not found
 */
export function getCountryName(countryCode, defaultCode = 'US') {
  const code = countryCode || defaultCode;
  const country = Country.getCountryByCode(code);
  return country ? country.name : code;
}
